import ControlFactory from '@/Factories/ControlFactory';
import GlobalConfiguration from '@/Management/GlobalConfiguration';
import ComponentManager from '@/Management/ComponentManager';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import ObjectLinkBoxViewModel from '@/ViewModels/Controls/ObjectLinkBoxViewModel';
import ObjectLinkBox from '@/Controls/ObjectLinkBox';
import AssociationBoxViewModel from '@/ViewModels/Controls/AssociationBoxViewModel';
import TextBoxViewModel from '@/ViewModels/Controls/TextBoxViewModel';
import HiddenViewModel from '@/ViewModels/Controls/HiddenViewModel';
import LinkViewModel from '@/ViewModels/Controls/LinkViewModel';
import ImageViewModel from '@/ViewModels/Controls/ImageViewModel';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import LocatorHelper from '@/Utility/LocatorHelper';
import DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';

export default class ObjectLinkBoxFactory extends ControlFactory {

    /**
     * @override
     */
    public context: DetailViewContext;
    private jQueryControl: JQuery;
    private ObjectLinkBoxViewModel = ComponentManager.getViewModel<typeof ObjectLinkBoxViewModel>('ObjectLinkBoxViewModel');
    private ObjectLinkBox = ComponentManager.getControl<typeof ObjectLinkBox>('ObjectLinkBox');
    private AssociationBoxViewModel = ComponentManager.getViewModel<typeof AssociationBoxViewModel>('AssociationBoxViewModel');
    private TextBoxViewModel = ComponentManager.getViewModel<typeof TextBoxViewModel>('TextBoxViewModel');
    private HiddenViewModel = ComponentManager.getViewModel<typeof HiddenViewModel>('HiddenViewModel');
    protected LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModel>('LinkViewModel');
    protected ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModel>('ImageViewModel');
    protected LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');

    /**
     * @override
     */
    public viewModel: ObjectLinkBoxViewModel;

    private objectValue: KnockoutObservable<any>;
    private allowSetOperation: boolean;

    public constructor(private params: any) {
        super(params);
        this.objectValue = this.context.genericObject.getObservable(this.detailViewItem.name);
        this.allowSetOperation = this.paramOrDefault(this.params.allowSetOperation, false);
    }

    /**
     * @override
     */
    control: ObjectLinkBox;

    /**
     * @override
     */
    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.viewModel = new this.ObjectLinkBoxViewModel({
            parentLocator: this.locator,
            locator: this.detailViewItem.name,
            factory: this,
            isRequired: this.isRequired,
            isEnabled: this.defineComputed(() => {
                return this.allowSetOperation && this.isEnabled();
            }),
            isValid: this.isValid,
            tooltip: new this.LabelViewModel({
                ergName: this.detailViewItem.tooltip
            }),
            associationBox: new this.AssociationBoxViewModel({
                parentLocator: this.locator,
                locator: LocatorHelper.buildLocator(this.detailViewItem.name, 'association'),
                textBox: new this.TextBoxViewModel({
                    parentLocator: this.locator,
                    locator: LocatorHelper.buildLocator(this.detailViewItem.name, 'association.value'),
                    value: this.defineComputed({
                        read: () => {
                            let value = this.objectValue() || '';
                            let split = value.split(',');

                            if (split.length !== 3) {
                                return value;
                            }

                            return split[2];
                        },
                        write: (val: any) => {
                            this.objectValue(val);
                        }
                    }),
                    isValid: this.isValid,
                    isFocusable: this.isFocusable,
                    invalidationText: this.invalidationText,
                    isEnabled: this.defineComputed(() => {
                        return this.allowSetOperation && this.isEnabled();
                    })
                }),
                hidden: new this.HiddenViewModel({
                    value: this.defineComputed(() => {
                        let value = this.objectValue() || '';
                        let split = value.split(',');

                        if (split.length !== 3) {
                            return '';
                        }

                        return split[1];
                    })
                }),
                linkEdit: new this.LinkViewModel({
                    parentLocator: this.locator,
                    locator: LocatorHelper.buildLocator(this.detailViewItem.name, 'association.edit'),
                    tooltip: new this.LabelViewModel({
                        ergName: 'EDIT_CONNECTED_TOOLTIP'
                    }),
                    image: new this.ImageViewModel({
                        parentLocator: this.locator,
                        locator: LocatorHelper.buildLocator(this.detailViewItem.name, 'association.edit.image'),
                        source: GlobalConfiguration.inlineImages.OBJ_EDIT,
                        alternativeText: new this.LabelViewModel({
                            ergName: 'EDIT_CONNECTED_TOOLTIP'
                        })
                    }),
                    isEnabled: this.defineComputed(() => {
                        let value = this.objectValue() || '';

                        let split = value.split(',');
                        if (split.length !== 3) return false;

                        let pdoId = split[1];
                        if (pdoId == this.detailViewItem.disableNavigation) return false;

                        return true;
                    }),
                    isFocusable: this.isFocusable,
                    onClick: () => {
                        let value = this.objectValue();
                        let split = value.split(',');
                        let listView = { typeName: split[0], types: [{ name: split[0] }], relationName: '' } // relationName MUST be set for uid navigation
                        let pdoId = split[1];

                        this.context.createContext(this, listView as any, pdoId);
                    },
                    isToolBarLink: true
                }),
                isEnabled: this.isEnabled
            })
        });

        this.control = new this.ObjectLinkBox(this.viewModel);

        let tuple = this.control.getTuple();
        this.jQueryControl = tuple.control;

        this.registerDefaultValidation([this.detailViewItem.name]);

        return this.jQueryControl;
    }
}

ComponentManager.setFactory('ObjectLinkBox', ObjectLinkBoxFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');